/**
 * REST client for the gateway's non-analytics endpoints (`/v1/...`)
 * — endpoint metadata, user/tenant management, provisioning tokens,
 * installer downloads, etc. The analytics graph isn't shaped right
 * for fixed-schema admin records, so those stay on the existing
 * REST surface.
 *
 * `authedFetch` is the shared bearer + 401-refresh wrapper. It's
 * defined here (the lower-level HTTP layer) and re-exported by
 * `analyticsClient.js` so the analytics + REST callers go through
 * one auth path.
 *
 * Bearer attachment moved into `authFetchShim.js` — calling code no
 * longer decorates `Authorization` itself. This wrapper exists for
 * (a) `AUTH_REQUIRED` gating, (b) the 401 → refresh → retry dance,
 * and (c) the typed `AuthMissingError` thrown when no token is in
 * the store before we even hit the wire.
 */
import { API_BASE_URL, AUTH_REQUIRED } from "./featureFlags.js";
import { grpcStatusName } from "./grpcStatus.js";
import {
    AuthMissingError,
    attemptRefresh,
    ensureFreshAccessToken,
    getAccessToken,
} from "./authBridge.js";
import { getAuthIdentityEpoch } from "./authTokenStore.js";
import { track } from "./analytics/client.js";
import { EVENT_NAME, REQUEST_ERROR_KIND } from "./analytics/events.js";

const API_BASE = API_BASE_URL.replace(/\/$/, "");

// Hard ceiling on any single request. Without it, a stalled connection (gateway
// not responding, a hung refresh upstream) leaves the fetch promise pending
// forever — and every caller awaiting it (a 120-row signal scan, the trace log,
// queued queries) hangs with it. On timeout the fetch rejects, so the stall
// surfaces as a normal error the caller can show/retry instead of a dead spin.
// Generous so legitimately slow analytics (getCommands runs 5–10s, sometimes
// more) isn't cut off.
const REQUEST_TIMEOUT_MS = 60000;

// fetch with an abort-timeout, composing with any caller-supplied signal.
function timedFetch(url, init = {}) {
    const ctrl = new AbortController();
    const timer = setTimeout(
        () => ctrl.abort(new DOMException("Request timed out", "TimeoutError")),
        REQUEST_TIMEOUT_MS,
    );
    const caller = init.signal;
    if (caller) {
        if (caller.aborted) ctrl.abort(caller.reason);
        else caller.addEventListener("abort", () => ctrl.abort(caller.reason), { once: true });
    }
    return fetch(url, { ...init, signal: ctrl.signal }).finally(() => clearTimeout(timer));
}

// Some gateway paths return 403 (not 401) on expired tokens; treat
// both as auth failures. A genuine authz denial will still 403 after
// refresh and bubble up to the caller.
const isAuthFailure = (res) => res.status === 401 || res.status === 403;

// Origin ids ride in URL paths as standard base64 (proto `bytes` → grpc-gateway
// JSON), which can contain `/` and `+`. A `/` splits the path and 404s at the
// gateway, and percent-encoding doesn't help — the gateway collapses %2F back to
// `/` before routing. base64url (`-`/`_`, padding kept) is a clean single
// segment, and grpc-gateway's bytes decoder falls back to URL-safe base64, so it
// round-trips to the same id. Use for any base64 id embedded in a request path.
export function idForPath(id) {
    return String(id).replace(/\+/g, "-").replace(/\//g, "_");
}

// -- Usage telemetry --------------------------------------------------------
//
// Every non-GET request that is not auth plumbing, a high-volume read, or a
// route the server already audits per call emits one `api_request` event:
// method, id-templated route, status and client-observed duration. This is a
// usage and latency signal for product analytics, never an audit record; who
// did what lives in the Postgres audit tables and the Loki audit dashboards.
const UNTRACKED_ROUTE_PREFIXES = [
    "/v1/auth/",
    "/v1/analytics",
    "/v1/me",
    "/v1/tenant/llm-proxy",
    "/v1/chats",
    // A canvas records every widget run and every open by itself; they are
    // bookkeeping, not actions, and would outnumber the actions people take.
    "/v1/tenant-views:record-",
];
const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Base64 ids are long and carry a digit, mixed case, or padding. Static route
// words such as `push-notifications` are long too but lowercase-only.
const OPAQUE_SEGMENT = /^(?=.*(?:\d|=|[A-Z].*[a-z]|[a-z].*[A-Z]))[A-Za-z0-9_=-]{16,}$/;
const NUMERIC_SEGMENT = /^\d+$/;

// `/v1/skills/<base64 id>:deploy?x=1` becomes `/v1/skills/:id:deploy`. Query
// strings are dropped; grpc-gateway custom verbs after the colon are kept.
export function templateRoute(path) {
    let pathname = String(path ?? "");
    try {
        pathname = new URL(pathname, "http://placeholder.invalid").pathname;
    } catch {
        // Keep the raw string; the segment rules below still apply.
    }
    return pathname
        .split("/")
        .map((segment) => {
            if (!segment) return segment;
            const colon = segment.indexOf(":");
            const head = colon === -1 ? segment : segment.slice(0, colon);
            const verb = colon === -1 ? "" : segment.slice(colon);
            const opaque = UUID_SEGMENT.test(head) || OPAQUE_SEGMENT.test(head) || NUMERIC_SEGMENT.test(head);
            return `${opaque ? ":id" : head}${verb}`;
        })
        .join("/");
}

// grpc-gateway custom verbs that are reads carried on POST bodies, such as
// `/v1/conversations:readRecordSummaries`, `:list-versions` or `:batchGet`.
// They are traffic, not actions. The verb's first word decides, in camel or
// kebab case, and a `batch` prefix defers to the word after it.
const READ_VERB_WORDS = new Set([
    "read", "list", "describe", "search", "query", "get", "lookup", "resolve",
    "preview", "estimate", "browse", "inspect", "download",
]);

function isReadVerb(route) {
    const verb = /:([A-Za-z][A-Za-z-]*)$/.exec(route)?.[1];
    if (!verb) return false;
    const words = verb.split(/-|(?=[A-Z])/).map((word) => word.toLowerCase());
    return READ_VERB_WORDS.has(words[0] === "batch" ? words[1] : words[0]);
}

export function shouldTrackRequest(method, route) {
    if (method === "GET" || method === "HEAD") return false;
    if (isReadVerb(route)) return false;
    return !UNTRACKED_ROUTE_PREFIXES.some((prefix) => route.startsWith(prefix));
}

function requestErrorKind(err) {
    if (err?.name === "TimeoutError") return REQUEST_ERROR_KIND.TIMEOUT;
    if (err?.name === "AbortError") return REQUEST_ERROR_KIND.ABORT;
    if (err?.name === "AuthMissingError") return REQUEST_ERROR_KIND.AUTH;
    return REQUEST_ERROR_KIND.NETWORK;
}

const nowMs = () => (typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now());

// `init.analytics` carries allowlisted extra properties (see the registry in
// lib/analytics/events.js) and is stripped before the request goes out.
// `init.freshToken` passes through to `authedFetchInner`.
export async function authedFetch(path, init = {}) {
    const { analytics: extras, ...fetchInit } = init ?? {};
    const method = String(fetchInit.method || "GET").toUpperCase();
    const route = templateRoute(path);
    if (!shouldTrackRequest(method, route)) return authedFetchInner(path, fetchInit);
    const started = nowMs();
    try {
        const res = await authedFetchInner(path, fetchInit);
        track(EVENT_NAME.API_REQUEST, {
            method,
            route,
            status: res.status,
            ok: res.ok,
            duration_ms: Math.round(nowMs() - started),
            ...(extras || {}),
        });
        return res;
    } catch (err) {
        track(EVENT_NAME.API_REQUEST, {
            method,
            route,
            status: 0,
            ok: false,
            error_kind: requestErrorKind(err),
            duration_ms: Math.round(nowMs() - started),
            ...(extras || {}),
        });
        throw err;
    }
}

// `freshToken: true` renews an access token that is about to expire before
// sending, for callers that poll on a timer and would otherwise pay a 401 and
// a retry at every expiry. The 401 → refresh → retry path still backs it up.
async function authedFetchInner(path, { freshToken = false, ...init } = {}) {
    const url = path.startsWith("http") ? path : `${API_BASE}${path}`;

    if (!AUTH_REQUIRED) return timedFetch(url, init);

    const requestIdentityEpoch = getAuthIdentityEpoch();
    const requestIdentityIsCurrent = () =>
        getAuthIdentityEpoch() === requestIdentityEpoch;

    // Restoring a missing access token always changes the identity epoch.
    // Recover credentials, then let callers issue a new request under that
    // identity instead of replaying this operation across the boundary.
    if (!getAccessToken()) {
        await attemptRefresh();
        throw new AuthMissingError();
    }
    if (freshToken) await ensureFreshAccessToken().catch(() => {});
    const sentToken = getAccessToken();
    let res = await timedFetch(url, init);
    if (!isAuthFailure(res)) return res;
    if (!requestIdentityIsCurrent()) return res;

    // Same-tab refresh race: a sibling authedFetch may have just
    // refreshed and advanced the store between our send and our
    // 401. Retry once if so before spending the refresh_token
    // ourselves.
    const currentToken = getAccessToken();
    if (currentToken && currentToken !== sentToken) {
        res = await timedFetch(url, init);
        if (!isAuthFailure(res)) return res;
        if (!requestIdentityIsCurrent()) return res;
    }

    const refreshed = await attemptRefresh();
    if (refreshed && requestIdentityIsCurrent()) {
        res = await timedFetch(url, init);
    }
    return res;
}

// ── JSON helpers ─────────────────────────────────────────────────

async function expectOk(res, label) {
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        // The gateway serializes failures as a gRPC-status envelope
        // ({ code, message, details }). Surface the human `message` as
        // `serverMessage` so callers can show it instead of the raw
        // "METHOD /path STATUS: {json}" string — which leaks request shape to
        // end users. `.message` keeps the developer-facing form for logs.
        let serverMessage = "";
        let grpcCode;
        let grpcStatusCode;
        let grpcDetails;
        try {
            const parsed = JSON.parse(text);
            if (parsed && typeof parsed.message === "string") serverMessage = parsed.message;
            // The envelope's numeric `code` is the only stable discriminator the
            // gateway gives us: several distinct refusals share one HTTP status,
            // and `message` is English prose a reword silently changes. Resolved
            // to its symbolic name so the dashboard has one vocabulary for the
            // field rather than bare integers spread across call sites.
            if (parsed && typeof parsed.code === "number") {
                grpcStatusCode = parsed.code;
                grpcCode = grpcStatusName(parsed.code);
            }
            if (parsed && Array.isArray(parsed.details)) grpcDetails = parsed.details;
        } catch {
            // Non-JSON body (proxy error page, empty) — leave serverMessage unset.
        }
        const err = new Error(`${label} ${res.status}: ${text || res.statusText}`);
        err.status = res.status;
        if (serverMessage) err.serverMessage = serverMessage;
        if (grpcCode !== undefined) err.grpcCode = grpcCode;
        if (grpcStatusCode !== undefined) err.grpcStatusCode = grpcStatusCode;
        if (grpcDetails !== undefined) err.grpcDetails = grpcDetails;
        throw err;
    }
    return res.json();
}

// `params` is a plain object; falsy values (null, undefined, empty
// string) are skipped so a default-only call doesn't pollute the URL.
export async function restGet(path, params = null, { signal } = {}) {
    let url = path;
    if (params) {
        const qs = new URLSearchParams();
        for (const [k, v] of Object.entries(params)) {
            if (v == null || v === "") continue;
            qs.append(k, String(v));
        }
        const qsString = qs.toString();
        if (qsString) url = `${path}?${qsString}`;
    }
    const res = await authedFetch(url, signal ? { signal } : undefined);
    return expectOk(res, `GET ${path}`);
}

// `analytics` is an optional bag of allowlisted extra properties for the
// `api_request` event (counts only, never names; see lib/analytics/events.js).
export async function restPost(path, body, { signal, analytics } = {}) {
    const res = await authedFetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body ?? {}),
        ...(signal ? { signal } : {}),
        ...(analytics ? { analytics } : {}),
    });
    return expectOk(res, `POST ${path}`);
}

export async function restPut(path, body, { analytics } = {}) {
    const res = await authedFetch(path, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body ?? {}),
        ...(analytics ? { analytics } : {}),
    });
    return expectOk(res, `PUT ${path}`);
}

export async function restPatch(path, body, { analytics } = {}) {
    const res = await authedFetch(path, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body ?? {}),
        ...(analytics ? { analytics } : {}),
    });
    return expectOk(res, `PATCH ${path}`);
}

export async function restDelete(path, { analytics } = {}) {
    const res = await authedFetch(path, { method: "DELETE", ...(analytics ? { analytics } : {}) });
    return expectOk(res, `DELETE ${path}`);
}
